import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import request from '../utils/request';
import { loadImagesFromAPI, ImageItem } from '../utils/imageUploadUtils';
import { processDesignImages } from '../utils/imageUtils';
import { SERVER_URL } from '../utils/request';

const BulkCreator: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [images, setImages] = useState<ImageItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [templateCode, setTemplateCode] = useState('');
  const [productTemplates, setProductTemplates] = useState<any[]>([]);
  const processedResultsRef = useRef<any[]>([]);
  const stackRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const initOnceRef = useRef(false);
  const [tagsList, setTagsList] = useState<string[]>([]);
  const computeProcessedResults = async (imgs: ImageItem[], templates: any[]) => {
    setIsLoading(true);
    processedResultsRef.current = [];
    for (const imgItm of imgs) {
      const src = (imgItm as any).url || (imgItm as any).src || '';
      if (!src) continue;
      for (const template of templates) {
        const designData = template.design;
        const r = await processDesignImages(src, { design: designData } as any);
        processedResultsRef.current.push({ src, result: r });
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const st = (location.state as any) || {};
    const incoming = Array.isArray(st.tags) ? st.tags : [];
    setTagsList(incoming);
  }, [location.state]);

  useEffect(() => {
    const loadImages = async () => {
      setIsLoading(true);
      try {
        if (initOnceRef.current && !tagsList.length) { setIsLoading(false); return; }
        initOnceRef.current = true;
        if (tagsList.length) {
          const result = await loadImagesFromAPI(1, '', 'all', 100, tagsList);
          if (productTemplates.length) {
            await computeProcessedResults(result.images, productTemplates);
          }
          setImages(result.images);
        }
      } catch {
      } finally {
        setIsLoading(false);
      }
    };
    loadImages();
    return () => {
      // 清理引用数据，避免内存占用
      processedResultsRef.current = [];
    };
  }, [tagsList]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  };


  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex flex-wrap items-center gap-2">
                {tagsList.map(t => (
                  <span key={t} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                    {t}
                    <button
                      className="ml-2 rounded-full px-1 hover:bg-gray-200"
                      onClick={async () => {
                        const next = tagsList.filter(x => x !== t);
                        setTagsList(next);
                        setIsLoading(true);
                        try {
                          const result = await loadImagesFromAPI(1, '', 'all', 100, next);
                          setImages(result.images);
                          if (productTemplates.length) {
                            await computeProcessedResults(result.images, productTemplates);
                          }
                        } catch { }
                        setIsLoading(false);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={templateCode}
                  onChange={(e) => setTemplateCode(e.target.value)}
                  placeholder="Template Code"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <input type="file" accept="image/*" onChange={handleInputChange} className="hidden" id="image-upload-lite" multiple />
                <button
                  onClick={async () => {
                    const code = templateCode.trim();
                    if (!code) return;
                    setIsLoading(true);
                    try {
                      const resp = await request({ url: `${SERVER_URL}/magic-boot/ai/getDesignTemplate?code=${encodeURIComponent(code)}`, method: 'GET' });
                      const tpl = resp?.result?.templates || [];
                      setProductTemplates(tpl);
                      if (tpl.length) {
                        await computeProcessedResults(images, tpl);
                      }
                    } catch { }
                    setIsLoading(false);
                  }}
                  className="px-3 py-2 bg-gray-800 text-white rounded cursor-pointer text-sm"
                >
                  Preview
                </button>
                <button
                  onClick={() => {
                    const refs = stackRefs.current;
                    const total = refs.length;
                    const imgCount = images.length;
                    const per = imgCount > 0 && total % imgCount === 0 ? total / imgCount : 1;
                    for (let i = 0; i < refs.length; i++) {
                      const el = refs[i];
                      if (!el) continue;
                      try {
                        const url = el.toDataURL('image/jpeg', 0.92);
                        const a = document.createElement('a');
                        a.href = url;
                        const baseIdx = imgCount > 0 ? Math.min(imgCount - 1, Math.floor(i / per)) : 0;
                        const seq = imgCount > 0 ? (i % per) + 1 : (i + 1);
                        const base = (images[baseIdx]?.name || 'preview').replace(/\.[^/.]+$/, '');
                        a.download = `${base}-${seq}.jpg`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      } catch { }
                    }
                  }}
                  className="px-3 py-2 bg-gray-800 text-white rounded cursor-pointer text-sm"
                >
                  Download
                </button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <p className="text-gray-600">Loading images...</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 gap-4">
              {processedResultsRef.current.map((item: { src: string; result: any }, idx: number) => {
                const pack = Array.isArray(item.result) ? item.result[0] : null;
                const imgs = pack && Array.isArray(pack.image) ? pack.image : [];
                return (
                  <div key={idx + 1} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="aspect-square">
                      <StackCanvas sources={imgs} size={400} register={(el) => { stackRefs.current[idx] = el; }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default BulkCreator;
const StackCanvas: React.FC<{ sources: string[]; size?: number; register?: (el: HTMLCanvasElement | null) => void }> = ({ sources, size = 400, register }) => {
  const ref = React.useRef<HTMLCanvasElement | null>(null);
  React.useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);
    const norm = (s: string) => s.replace(/`/g, '').trim();
    const list = (sources || []).map(norm).filter(Boolean);
    let cancelled = false;
    const draw = async () => {
      for (const src of list) {
        const img = new Image();
        // if (src.startsWith('data:')) continue;
        let tempSrc = src;
        if (!src.startsWith('data:')) {
          // continue;
          tempSrc += "?x-oss-process=style/large";
          img.crossOrigin = 'anonymous';
        }
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = tempSrc;
        });
        if (cancelled) return;
        const iw = img.width || 1;
        const ih = img.height || 1;
        const aspectImg = iw / ih;
        const aspectDest = 1;
        let dw = size, dh = size;
        if (aspectImg > aspectDest) {
          dh = size / aspectImg;
          dw = size;
        } else {
          dw = size * aspectImg;
          dh = size;
        }
        const dx = (size - dw) / 2;
        const dy = (size - dh) / 2;
        ctx.drawImage(img, dx, dy, dw, dh);
      }
    };
    draw();
    return () => { cancelled = true; };
  }, [sources, size]);
  return <canvas ref={(el) => { ref.current = el; register?.(el); }} className="w-full h-full" />;
};
